/**
 * periodAnalysis.js : analyse d'une période d'une session (pur, testable).
 *
 * Pourquoi : dans un enregistrement de 24 h, isoler un transport de 8 h et
 * lire statistiques, alertes et interprétation sur ces 8 h seules. Un seul
 * objet, produit ici, nourrit tous les blocs de la page de session (et le PDF
 * au Lot 2) : aucun bloc ne recalcule de son côté.
 *
 * Sans période (window null), l'objet porte la session telle que stockée :
 * mêmes agrégats qu'avant (page inchangée), plus l'exposition (médiane, temps
 * au-dessus des seuils) calculée sur toutes les mesures.
 *
 * Bornes en millisecondes epoch, jamais en heure locale (D201) : une période
 * qui traverse minuit ou le changement d'heure garde sa durée réelle.
 * Calculs délégués aux fonctions du Lot 0 (statsUtils, D235) et à
 * analysisRange (filtre, couverture, seuils de confiance de D223).
 */
import pmData from '../../constants/pmThresholds.json';
import RULE from '../../constants/estimatedTimestampsRule.json';
import { computeMinMaxAvg, computeStepMin, hoursAbove, isFiniteNumber, percentile } from '../../utils/statsUtils';
import { coverageRatio, countMeasured, filterReadings, MIN_COVERAGE, MIN_MEASURES_FOR_BEST } from '../analysisRange';

// Durée minimale d'une période : en dessous, une moyenne ne dit plus rien d'une
// exposition (cohérent avec la catégorie « courte » du résumé, < 30 min).
export const MIN_PERIOD_MS = 30 * 60000;

/** Raisons pour lesquelles une période demandée n'est pas appliquée. */
export const PERIOD_ERRORS = Object.freeze({
  INVALID: 'invalid',
  TOO_SHORT: 'tooShort',
  NO_PM: 'noPm',
});

/** Avertissements d'une période appliquée. */
export const PERIOD_WARNINGS = Object.freeze({
  LOW_COVERAGE: 'lowCoverage',
  FEW_MEASURES: 'fewMeasures',
  ESTIMATED_TIME: 'estimatedTime',
});

/** Grandeurs PM : clé de seuil (pmThresholds.json) et champ de mesure. */
export const PM_FIELDS = Object.freeze([
  { key: 'pm1', field: 'pm1', aggregate: 'pm1' },
  { key: 'pm25', field: 'pm2_5', aggregate: 'pm2_5' },
  { key: 'pm10', field: 'pm10', aggregate: 'pm10' },
]);

/**
 * CO2 (D253, lot 2) : troisième famille à côté des PM et de l'environnement.
 * Présent seulement sur les sessions importées depuis un capteur externe
 * (PMReading.co2, Session.co2_min/avg/max) ; jamais sur EkynAir Breathe,
 * EkynAir Live ni le mobile. Une seule référence d'affichage, la valeur max
 * recommandée, non alertante.
 */
export const CO2_FIELD = Object.freeze({ key: 'co2', field: 'co2', aggregate: 'co2' });

// Agrégats stockés sur Session et recalculés sur la fenêtre (même forme).
const AGGREGATES = [
  ...PM_FIELDS.map(({ field, aggregate }) => ({ field, prefix: aggregate })),
  { field: 'temperature', prefix: 'temp' },
  { field: 'humidity', prefix: 'humidity' },
  { field: CO2_FIELD.field, prefix: CO2_FIELD.aggregate },
];

/**
 * Une session (ou une période) a du CO2 si son agrégat co2_avg est mesuré ou
 * si au moins une mesure porte un co2. Seul juge de la présence du CO2 pour la
 * carte, la courbe et l'analyse de période : sans CO2, rien n'est affiché,
 * pas même « non mesuré » (choix du lot 2, à l'inverse du PM10 d'un capteur
 * externe, grandeur attendue de tout capteur).
 */
export function hasCo2(session, readings) {
  if (isFiniteNumber(session?.co2_avg)) return true;
  return (readings || []).some(r => isFiniteNumber(r?.co2));
}

const toMs = (date) => (date == null ? NaN : new Date(date).getTime());

/**
 * Horodatage estimé : sessions EkynAir Live (dates extrapolées par EkyMind) et
 * sessions EkynAir Breathe envoyées après un enregistrement hors ligne (grille
 * reconstruite au transfert). Le mode déclaré ne suffit pas : une synchro hors
 * ligne réelle est enregistrée avec mode « normal » et un localUuid « offline-… ».
 * Règle en données dans shared/estimatedTimestampsRule.json (D247), lue aussi
 * par le serveur pour le lien de séance public ; même table de cas des deux côtés.
 * La page du lien reçoit le résultat déjà calculé (`estimatedTimestamps`, sans
 * les champs bruts) : il prime quand il est présent.
 */
export function hasEstimatedTimestamps(session) {
  if (!session) return false;
  if (typeof session.estimatedTimestamps === 'boolean') return session.estimatedTimestamps;
  if (RULE.liveSensorTypes.includes(session.sensorType) || RULE.liveSources.includes(session.source)) return true;
  if (session.source !== RULE.mobileSource) return false;
  return RULE.offlineModes.includes(session.mobileMetadata?.mode)
    || (typeof session.localUuid === 'string' && session.localUuid.startsWith(RULE.offlineLocalUuidPrefix));
}

/**
 * Durée de la session affichée « sur une session de Y h » : start_time → end_time,
 * comme l'en-tête et la carte Durée de la page ; étendue des mesures à défaut.
 */
export function sessionDurationMs(session, readings) {
  const start = toMs(session?.start_time);
  const end = toMs(session?.end_time);
  if (Number.isFinite(start) && Number.isFinite(end) && end >= start) return end - start;
  const list = readings || [];
  return list.length ? toMs(list[list.length - 1].timestamp) - toMs(list[0].timestamp) : 0;
}

/**
 * Bornes sélectionnables : union de start_time/end_time et de l'étendue des
 * mesures, pour ne jamais exclure une mesure (end_time peut précéder ou
 * dépasser la dernière mesure). null si aucune borne connue.
 */
export function sessionBounds(session, readings) {
  const list = readings || [];
  const candidates = [
    [toMs(session?.start_time), toMs(session?.end_time)],
    list.length ? [toMs(list[0].timestamp), toMs(list[list.length - 1].timestamp)] : [NaN, NaN],
  ];
  const starts = candidates.map(c => c[0]).filter(Number.isFinite);
  const ends = candidates.map(c => c[1]).filter(Number.isFinite);
  if (!starts.length || !ends.length) return null;
  const start = Math.min(...starts);
  const end = Math.max(...ends);
  return end > start ? { start, end } : null;
}

/** Fenêtre ramenée aux bornes ; null si illisible ou vide après bornage. */
export function clampWindow(window, bounds) {
  if (!window || !bounds) return null;
  const from = Number(window.start);
  const to = Number(window.end);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const start = Math.max(bounds.start, from);
  const end = Math.min(bounds.end, to);
  return start < end ? { start, end } : null;
}

/**
 * Exposition par grandeur PM sur des mesures : médiane, P95, nombre de valeurs,
 * temps au-dessus des seuils d'avertissement et d'alerte (durée au pas médian,
 * D198) et sa part du temps mesuré. La part du temps mesuré vaut la part des
 * mesures au-dessus, puisque chaque mesure compte pour un pas.
 * `hours` : hoursAbove, arrondi à 0,1 h comme le serveur ; `minutes` : même
 * calcul (mesures × pas) sans arrondi, pour un affichage à la minute (sur une
 * période courte, 0,1 h ferait lire 12 min pour 10).
 */
export function computeExposure(readings, stepMin) {
  const pm = Object.fromEntries(PM_FIELDS.map(({ key, field }) => {
    const values = (readings || []).map(r => r[field]).filter(isFiniteNumber);
    const measured = values.length;
    const above = (threshold) => {
      const count = values.filter(v => v > threshold).length;
      return {
        threshold,
        hours: hoursAbove(readings, field, threshold, stepMin),
        minutes: measured ? count * stepMin : null,
        pct: measured ? (count / measured) * 100 : null,
      };
    };
    const { warningMax, alertMax } = pmData.PM_THRESHOLDS[key];
    return [key, {
      measured,
      median: percentile(values, 50),
      p95: percentile(values, 95),
      warning: above(warningMax),
      alert: above(alertMax),
    }];
  }));
  return { ...pm, [CO2_FIELD.key]: computeCo2Exposure(readings, stepMin) };
}

/**
 * Famille CO2 : moyenne, max, P95 (percentile unique, D200) et temps au-dessus
 * de la valeur max recommandée, au même pas que les PM (chaque mesure vaut un
 * pas : sur une session d'un capteur externe, toutes les lectures gardées
 * portent un CO2 moyenné par fenêtre, le pas médian est donc celui du CO2).
 * Tout à null sans CO2 mesuré ; un vrai 0 reste une valeur (D190).
 */
export function computeCo2Exposure(readings, stepMin) {
  const values = (readings || []).map(r => r?.[CO2_FIELD.field]).filter(isFiniteNumber);
  const measured = values.length;
  const threshold = pmData.CO2_THRESHOLDS.referenceMax;
  const { avg, max } = computeMinMaxAvg(values);
  const count = values.filter(v => v > threshold).length;
  return {
    measured,
    avg,
    max,
    p95: percentile(values, 95),
    above: {
      threshold,
      minutes: measured ? count * stepMin : null,
      pct: measured ? (count / measured) * 100 : null,
    },
  };
}

/** Session de même forme que Session, limitée à la fenêtre. */
function windowedSession(session, readings, window) {
  const aggregates = {};
  for (const { field, prefix } of AGGREGATES) {
    const { min, max, avg } = computeMinMaxAvg(readings.map(r => r[field]));
    aggregates[`${prefix}_min`] = min;
    aggregates[`${prefix}_avg`] = avg;
    aggregates[`${prefix}_max`] = max;
  }
  return {
    ...session,
    ...aggregates,
    start_time: new Date(window.start).toISOString(),
    end_time: new Date(window.end).toISOString(),
    duration_min: Math.round((window.end - window.start) / 60000),
    data_count: readings.length,
  };
}

const hasPm = (readings) => PM_FIELDS.some(({ field }) => countMeasured(readings, field) > 0);

/**
 * Analyse de la page de session.
 * @param {Object} session — document Session (agrégats stockés)
 * @param {Array} readings — toutes les mesures, triées (fetchAllReadings)
 * @param {{start:number, end:number}|null} window — période demandée (ms epoch)
 * @param {{ total?: number, gapReferenceMs?: number, bounds?: {start,end} }} [options]
 *   Réservées à la page du lien de séance (D247), qui ne reçoit que les mesures
 *   de la période : nombre de mesures de toute la séance, intervalle médian de
 *   toute la séance (plafond des trous de la couverture), bornes sélectionnables
 *   de toute la séance. Absentes : calculés sur `readings` (fiche inchangée).
 * @returns {{
 *   active: boolean, error: string|null, requested: {start,end}|null,
 *   window: {start,end}|null, session: Object, readings: Array,
 *   count: number, total: number, coverage: number|null,
 *   durationMs: number, sessionDurationMs: number,
 *   exposure: Object, hiddenP95: string[], warnings: string[]
 * }}
 *   `session` est la session fenêtrée (ou la session stockée sans période) ;
 *   `hiddenP95` liste les grandeurs dont le P95 est masqué (moins de 30 valeurs).
 */
export function buildPeriodAnalysis(session, readings, window, options = {}) {
  const all = readings || [];
  const total = Number.isFinite(options.total) ? options.total : all.length;
  const stepOf = (list) => computeStepMin(list, session?.mobileMetadata?.acquisitionInterval);
  const sessionMs = sessionDurationMs(session, all);
  const whole = (error = null, requested = null) => ({
    active: false, error, requested, window: null, session, readings: all,
    count: all.length, total, coverage: null,
    durationMs: sessionMs, sessionDurationMs: sessionMs,
    exposure: computeExposure(all, stepOf(all)), hiddenP95: [], warnings: [],
  });
  if (!window) return whole();

  const clamped = clampWindow(window, options.bounds || sessionBounds(session, all));
  if (!clamped) return whole(PERIOD_ERRORS.INVALID, null);
  if (clamped.end - clamped.start < MIN_PERIOD_MS) return whole(PERIOD_ERRORS.TOO_SHORT, clamped);
  const inside = filterReadings(all, clamped);
  if (!hasPm(inside)) return whole(PERIOD_ERRORS.NO_PM, clamped);

  const coverage = coverageRatio(all, clamped, options.gapReferenceMs);
  const exposure = computeExposure(inside, stepOf(inside));
  const hiddenP95 = PM_FIELDS
    .filter(({ key }) => exposure[key].measured > 0 && exposure[key].measured < MIN_MEASURES_FOR_BEST)
    .map(({ key }) => key);
  const warnings = [
    coverage < MIN_COVERAGE && PERIOD_WARNINGS.LOW_COVERAGE,
    hiddenP95.length > 0 && PERIOD_WARNINGS.FEW_MEASURES,
    hasEstimatedTimestamps(session) && PERIOD_WARNINGS.ESTIMATED_TIME,
  ].filter(Boolean);

  return {
    active: true, error: null, requested: clamped, window: clamped,
    session: windowedSession(session, inside, clamped), readings: inside,
    count: inside.length, total, coverage,
    durationMs: clamped.end - clamped.start, sessionDurationMs: sessionMs,
    exposure, hiddenP95, warnings,
  };
}
